// The panes' sizes (docs/IDE_PARITY_AUDIT.md, "Web layout"): splitters
// between the files and the editor, the editor and the program, the editor
// and its console, and the program and the functions under it, as the Mac
// IDE's split views have. Each is dragged,
// or moved with the arrow keys when it has the focus; a double click puts it
// back. The sizes are kept for next time.
//
// The sizes are CSS variables on <html>, which index.html's first script sets
// from the same storage before the page is drawn, so that it opens at them.

export const SIZES = "b3d-ide:sizes";

const DEFAULTS = { files: 212, pane: 0.5, console: null, symbols: null };
// How far an arrow key moves a splitter, and with Shift held.
const STEP = 16, BIG_STEP = 64;

export function readSizes(storage = localStorage) {
  try {
    const saved = JSON.parse(storage.getItem(SIZES));
    return { ...DEFAULTS, ...(saved && typeof saved === "object" ? saved : {}) };
  } catch {
    return { ...DEFAULTS };
  }
}

// The CSS variables for a set of sizes. The program pane's share of the room
// beside the files is a fraction, so that it keeps its proportion as the
// window resizes; the files and the console keep their pixels.
export function applySizes(sizes, root = document.documentElement) {
  const style = root.style;
  style.setProperty("--files-width", `${Math.round(sizes.files)}px`);
  style.setProperty("--editor-share", `${1 - sizes.pane}fr`);
  style.setProperty("--pane-share", `${sizes.pane}fr`);
  if (sizes.console == null) style.removeProperty("--console-height");
  else style.setProperty("--console-height", `${Math.round(sizes.console)}px`);
  if (sizes.symbols == null) style.removeProperty("--symbols-height");
  else style.setProperty("--symbols-height", `${Math.round(sizes.symbols)}px`);
}

const clamp = (value, low, high) => Math.min(Math.max(value, low), Math.max(low, high));

export function setupSplitters({ $, onResize = () => {} }) {
  const sizes = readSizes();
  const save = () => {
    try { localStorage.setItem(SIZES, JSON.stringify(sizes)); } catch {}
  };

  // Each splitter: which size it sets, from a pointer's position or by a
  // step, the limits of that size, and its value for assistive technology.
  // The console's, or the debugger's in its place.
  const bottomHeight = () => $("console").offsetHeight || $("debugger").offsetHeight;
  const splitters = [
    {
      element: $("split-files"),
      key: "files",
      horizontal: false,
      limits: () => [120, Math.min(480, $("main").clientWidth * 0.4)],
      fromPointer: (event) => event.clientX - $("main").getBoundingClientRect().left,
      step: (delta) => sizes.files + delta,
      value: () => Math.round(sizes.files),
    },
    {
      element: $("split-pane"),
      key: "pane",
      horizontal: false,
      // At least 240 pixels each for the editor and the program.
      limits: () => {
        const room = $("main").getBoundingClientRect().right - $("center").getBoundingClientRect().left;
        return room > 480 ? [240 / room, 1 - 240 / room] : [0.5, 0.5];
      },
      fromPointer: (event) => {
        const right = $("main").getBoundingClientRect().right;
        return (right - event.clientX) / (right - $("center").getBoundingClientRect().left);
      },
      step: (delta) => {
        const room = $("main").getBoundingClientRect().right - $("center").getBoundingClientRect().left;
        return sizes.pane - delta / room;
      },
      value: () => Math.round(sizes.pane * 100),
    },
    {
      element: $("split-console"),
      key: "console",
      horizontal: true,
      // The editor keeps at least 80 pixels.
      limits: () => [40, $("center").clientHeight - 80 - $("console-bar").offsetHeight],
      fromPointer: (event) => $("center").getBoundingClientRect().bottom - event.clientY -
        $("console-bar").offsetHeight,
      step: (delta) => (sizes.console ?? bottomHeight()) - delta,
      value: () => Math.round(sizes.console ?? bottomHeight()),
    },
    {
      element: $("split-symbols"),
      key: "symbols",
      horizontal: true,
      // The program keeps at least 120 pixels.
      limits: () => [60, $("right").clientHeight - 120 - $("program-tabs").offsetHeight],
      fromPointer: (event) => $("right").getBoundingClientRect().bottom - event.clientY,
      step: (delta) => (sizes.symbols ?? $("symbol-pane").offsetHeight) - delta,
      value: () => Math.round(sizes.symbols ?? $("symbol-pane").offsetHeight),
    },
  ];

  const set = (splitter, value) => {
    sizes[splitter.key] = clamp(value, ...splitter.limits());
    applySizes(sizes);
    splitter.element.setAttribute("aria-valuenow", String(splitter.value()));
    onResize();
  };

  for (const splitter of splitters) {
    const { element } = splitter;
    element.setAttribute("aria-valuenow", String(splitter.value()));
    element.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      element.setPointerCapture(event.pointerId);
      // The program's and the help's frames would take the pointer from the
      // splitter as it passes over them.
      document.body.classList.add("resizing", splitter.horizontal ? "resizing-rows" : "resizing-columns");
      const move = (moved) => set(splitter, splitter.fromPointer(moved));
      const end = () => {
        element.removeEventListener("pointermove", move);
        element.removeEventListener("pointerup", end);
        element.removeEventListener("pointercancel", end);
        document.body.classList.remove("resizing", "resizing-rows", "resizing-columns");
        save();
      };
      element.addEventListener("pointermove", move);
      element.addEventListener("pointerup", end);
      element.addEventListener("pointercancel", end);
    });
    element.addEventListener("dblclick", () => {
      sizes[splitter.key] = DEFAULTS[splitter.key];
      applySizes(sizes);
      element.setAttribute("aria-valuenow", String(splitter.value()));
      save();
      onResize();
    });
    // Left and Up make the pane before the splitter smaller.
    element.addEventListener("keydown", (event) => {
      const forward = splitter.horizontal ? "ArrowDown" : "ArrowRight";
      const back = splitter.horizontal ? "ArrowUp" : "ArrowLeft";
      if (event.key !== forward && event.key !== back) return;
      event.preventDefault();
      const step = (event.shiftKey ? BIG_STEP : STEP) * (event.key === forward ? 1 : -1);
      set(splitter, splitter.step(step));
      save();
    });
  }

  applySizes(sizes);
  return {
    sizes,
    // Every pane back to its first size.
    reset() {
      Object.assign(sizes, DEFAULTS);
      applySizes(sizes);
      for (const splitter of splitters) splitter.element.setAttribute("aria-valuenow", String(splitter.value()));
      save();
      onResize();
    },
  };
}
